import { Box, Group, Stack } from "@mantine/core";

import { Button, NumberInput, Text } from "components";

import classes from "./TargetsPage.module.css";

export type AgentTargetMetricConfig = {
  label: string;
  metric: string;
  placeholder: string;
  plainInput: boolean;
};

type AgentTargetValues = Record<string, number | "">;

export type AgentTargetFormRow = {
  agentId: number;
  agentName: string;
  targets: AgentTargetValues;
  extraTargets?: Array<{
    metric: string;
    value: number;
  }>;
};

interface AgentTargetsSectionProps {
  agents: AgentTargetFormRow[];
  emptyLabel: string;
  hint: string;
  isDisabled: boolean;
  isSaving: boolean;
  metrics: AgentTargetMetricConfig[];
  onTargetChange: (agentId: number, metric: string, value: string | number) => void;
  onSave: () => void;
  saveLabel: string;
  showEmptyState: boolean;
  title: string;
}

const AgentTargetsSection = ({
  agents,
  emptyLabel,
  hint,
  isDisabled,
  isSaving,
  metrics,
  onTargetChange,
  onSave,
  saveLabel,
  showEmptyState,
  title,
}: AgentTargetsSectionProps) => (
  <section className={classes.panel}>
    <Text className={classes.sectionTitle}>{title}</Text>

    <Stack gap="md" mt="md">
      {agents.map((agent) => (
        <div key={agent.agentId} className={`${classes.targetCard} ${classes.agentTargetCard}`}>
          <Box className={classes.targetIdentity}>
            <Text className={classes.targetName}>{agent.agentName}</Text>
            <Text className={classes.targetHint}>{hint}</Text>
          </Box>

          <div className={classes.agentInputs}>
            {metrics.map((metric) => (
              <div key={metric.metric} className={classes.inputWrap}>
                <NumberInput
                  label={metric.label}
                  placeholder={metric.placeholder}
                  value={agent.targets[metric.metric] ?? ""}
                  onChange={(value) => onTargetChange(agent.agentId, metric.metric, value)}
                  plain={metric.plainInput}
                  min={0}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {showEmptyState && agents.length === 0 ? (
        <div className={classes.sectionEmptyState}>
          <Text className={classes.targetHint}>{emptyLabel}</Text>
        </div>
      ) : null}
    </Stack>

    <Group justify="flex-end" align="center" mt="lg" className={classes.footer}>
      <Button
        disabled={isDisabled}
        loading={isSaving}
        radius="md"
        size="md"
        className={classes.saveButton}
        onClick={onSave}
      >
        {saveLabel}
      </Button>
    </Group>
  </section>
);

export default AgentTargetsSection;
